// pages/student/components/UserNavbar.js
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  Button,
  Avatar,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BookIcon from "@mui/icons-material/Book";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import Logo from "../../../assets/Header/logo2.png";

const navLinks = [
  { text: "Home", path: "/" },
  { text: "My Courses", path: "/my-courses" },
  { text: "Explore", path: "/courses" },
  { text: "About", path: "/about" },
];

const UserNavbar = () => {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const data = JSON.parse(user);
        setUserInfo({
          name: data.firstname || "User",
          email: data.email || "",
          avatar: data.avatar || "",
        });
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/");
    window.location.reload();
  };

  const handleMenuItemClick = (action) => {
    handleCloseUserMenu();
    switch (action) {
      case "Dashboard":
        navigate("/dashboard");
        break;
      case "My Profile":
        navigate("/profile");
        break;
      case "My Courses":
        navigate("/my-courses");
        break;
      case "Logout":
        handleLogout();
        break;
      default:
        break;
    }
  };

  const userMenuItems = [
    { label: "Dashboard", icon: <DashboardIcon /> },
    { label: "My Courses", icon: <BookIcon /> },
    { label: "My Profile", icon: <PersonIcon /> },
    { divider: true },
    { label: "Logout", icon: <LogoutIcon />, danger: true },
  ];

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: "#ffffff",
        borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        backdropFilter: "blur(10px)",
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ height: 72 }}>
          {/* Logo - Desktop */}
          <Typography
            variant="h6"
            noWrap
            component={NavLink}
            to="/"
            sx={{
              mr: 4,
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
              flexShrink: 0,
            }}
          >
            <img
              src={Logo}
              alt="CourseHub"
              style={{ height: 40, width: "auto" }}
            />
          </Typography>

          {/* Mobile Menu */}
          <Box sx={{ display: { xs: "flex", md: "none" }, flex: 1 }}>
            <IconButton
              size="large"
              aria-label="menu"
              onClick={handleOpenNavMenu}
              sx={{ color: "text.primary" }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: "block", md: "none" },
                "& .MuiPaper-root": {
                  borderRadius: 2,
                  mt: 1,
                  minWidth: 200,
                },
              }}
            >
              {navLinks.map((link) => (
                <MenuItem
                  key={link.text}
                  onClick={handleCloseNavMenu}
                  component={NavLink}
                  to={link.path}
                  sx={{
                    py: 1.5,
                    px: 3,
                    "&:hover": {
                      backgroundColor: "primary.light",
                      color: "primary.main",
                    },
                  }}
                >
                  <Typography textAlign="center" fontWeight={500}>
                    {link.text}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Logo - Mobile */}
          <Typography
            variant="h5"
            noWrap
            component={NavLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: "flex", md: "none" },
              flexGrow: 1,
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <img
              src={Logo}
              alt="CourseHub"
              style={{ height: 32, width: "auto" }}
            />
          </Typography>

          {/* Desktop Navigation */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "none", md: "flex" },
              gap: 1,
            }}
          >
            {navLinks.map((link) => (
              <Button
                key={link.text}
                component={NavLink}
                to={link.path}
                onClick={handleCloseNavMenu}
                sx={{
                  color: "text.primary",
                  px: 3,
                  py: 1,
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "primary.light",
                    color: "primary.main",
                  },
                  "&.active": {
                    color: "primary.main",
                    backgroundColor: "primary.light",
                  },
                }}
              >
                {link.text}
              </Button>
            ))}
          </Box>

          {/* User Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, md: 2 },
            }}
          >
            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton sx={{ color: "text.secondary" }}>
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* User Menu */}
            <Tooltip title="Account settings">
              <Box
                onClick={handleOpenUserMenu}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  p: 0.5,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Avatar
                  alt={userInfo?.name}
                  src={userInfo?.avatar || ""}
                  sx={{ width: 32, height: 32 }}
                />
                {!isMobile && (
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color="text.primary"
                  >
                    {userInfo?.name}
                  </Typography>
                )}
                <ExpandMoreIcon
                  sx={{ color: "text.secondary", fontSize: 20 }}
                />
              </Box>
            </Tooltip>

            <Menu
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
              onClick={handleCloseUserMenu}
              sx={{
                mt: "45px",
                "& .MuiPaper-root": {
                  borderRadius: 2,
                  minWidth: 200,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "top" }}
            >
              {/* User Info Header */}
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {userInfo?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {userInfo?.email}
                </Typography>
              </Box>
              <Divider />

              {userMenuItems.map((item, index) => {
                if (item.divider) {
                  return <Divider key={`divider-${index}`} />;
                }
                return (
                  <MenuItem
                    key={item.label}
                    onClick={() => handleMenuItemClick(item.label)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      gap: 1.5,
                      color: item.danger ? "error.main" : "text.primary",
                      "&:hover": {
                        backgroundColor: item.danger
                          ? "error.light"
                          : "action.hover",
                      },
                    }}
                  >
                    {item.icon}
                    <Typography variant="body2">{item.label}</Typography>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default UserNavbar;
